import { useMemo } from 'react';
import { format, differenceInCalendarDays } from 'date-fns';
import { formatCalendarDate } from '../utils/date-time';
import { useBookingStore } from '../store/useBookingStore';
import { useServiceQuery } from '../queries';

/**
 * Hook to get current service from store + query
 */
export function useService() {
	const currentServiceId = useBookingStore((state) => state.currentServiceId);
	return useServiceQuery(
		{ id: currentServiceId },
		{ enabled: currentServiceId > 0 }
	);
}

/**
 * Hook to compute if the service is a date range booking
 */
export function useIsDateRange(): boolean {
	const { data: service } = useService();
	return useMemo(() => {
		return Boolean(service?.blockSize && service.blockSize >= 1440);
	}, [service?.blockSize]);
}

/**
 * Hook to compute the booking time value string for the hidden input.
 * Always outputs range format: "start|end"
 *
 * For date-range-end-inclusive mode (full-day bookings, not nightly):
 * - End time is set to 23:59:59 of the last selected day
 * - This makes the end date self-documenting (the date IS the last day)
 *
 * For nightly mode or datetime-range:
 * - Uses the actual times from the selected range
 */
export function useBookingTimeValue(): string {
	const { data: service } = useService();
	const selectedRange = useBookingStore((state) => state.selectedRange);

	return useMemo(() => {
		if (!service || !selectedRange?.from || !selectedRange?.to) {
			return '';
		}

		// Format dates in YYYY-MM-DD format
		const startDate = formatCalendarDate(selectedRange.from);
		const endDate = formatCalendarDate(selectedRange.to);

		// Check if this is a date-range booking (full-day blocks)
		const isDateRange = service.blockSize >= 1440;
		const isNightlyMode = service.treatAsNights;

		if (isDateRange && !isNightlyMode) {
			// date-range-end-inclusive: end time is 23:59:59 of the last day
			// This makes the end date self-documenting - the date IS the last day of the booking
			return `${startDate} 00:00:00|${endDate} 23:59:59`;
		}

		// For nightly mode or datetime-range, use the actual times
		const startTime = format(selectedRange.from, 'HH:mm:ss');
		const endTime = format(selectedRange.to, 'HH:mm:ss');
		return `${startDate} ${startTime}|${endDate} ${endTime}`;
	}, [service, selectedRange]);
}

/**
 * Hook to get the selected date (computed from range)
 */
export function useSelectedDate(): Date | undefined {
	const selectedRange = useBookingStore((state) => state.selectedRange);
	return useMemo(() => selectedRange?.from, [selectedRange]);
}

/**
 * Hook to get the selected time (computed from range)
 */
export function useSelectedTime(): string | undefined {
	const selectedRange = useBookingStore((state) => state.selectedRange);
	return useMemo(() => {
		const from = selectedRange?.from;
		if (!from) {
			return undefined;
		}
		// Return HH:MM format using date-fns
		return format(from, 'HH:mm');
	}, [selectedRange]);
}

/**
 * Hook to get the selected duration (computed from range)
 */
export function useSelectedDuration(): number {
	const { data: service } = useService();
	const selectedRange = useBookingStore((state) => state.selectedRange);

	return useMemo(() => {
		if (!selectedRange?.from || !selectedRange?.to) {
			return 0;
		}

		const isDateRange = Boolean(
			service?.blockSize && service.blockSize >= 1440
		);

		// For day/night range modes, use calendar-day math to avoid DST under/over counts.
		if (isDateRange) {
			const calendarDays = Math.max(
				0,
				differenceInCalendarDays(selectedRange.to, selectedRange.from)
			);
			return service?.treatAsNights
				? calendarDays * 1440
				: (calendarDays + 1) * 1440;
		}

		// Datetime-range mode uses actual elapsed minutes.
		return Math.round(
			(selectedRange.to.getTime() - selectedRange.from.getTime()) /
				(1000 * 60)
		);
	}, [selectedRange, service?.blockSize, service?.treatAsNights]);
}

/**
 * Hook to compute the effective duration
 */
export function useEffectiveDuration(): number {
	const { data: service } = useService();
	const selectedDuration = useSelectedDuration();

	return useMemo(() => {
		if (selectedDuration > 0) {
			return selectedDuration;
		}
		return service?.minDuration ?? service?.blockSize ?? 0;
	}, [selectedDuration, service?.minDuration, service?.blockSize]);
}

/**
 * Hook to get the original booking time in HH:mm format (for entry edit mode).
 */
export function useOriginalTime(): string | undefined {
	const originalRange = useBookingStore((state) => state.originalRange);
	return useMemo(() => {
		const from = originalRange?.from;
		if (!from) {
			return undefined;
		}
		return format(from, 'HH:mm');
	}, [originalRange]);
}
